import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Calendar, Plus, Lock, Unlock, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function PayPeriods() {
  const { api } = useAuth();
  const [payPeriods, setPayPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  const fetchPayPeriods = async () => {
    setLoading(true);
    try {
      const response = await api.get('/timesheets/pay-periods');
      setPayPeriods(response.data);
    } catch (error) {
      console.error('Pay periods fetch error:', error);
      let errorMsg = 'Failed to fetch pay periods';
      if (error.response?.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Access denied. Admin privileges required.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await api.post('/timesheets/pay-periods', formData);
      toast.success('Pay period created');
      setCreateDialogOpen(false);
      setFormData({ name: '', start_date: '', end_date: '' });
      fetchPayPeriods();
    } catch (error) {
      console.error('Create pay period error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pay period');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (period) => {
    const newStatus = period.status === 'open' ? 'closed' : 'open';
    try {
      await api.patch(`/timesheets/pay-periods/${period.id}?status=${newStatus}`);
      toast.success(`Pay period ${newStatus}`);
      fetchPayPeriods();
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pay period');
    }
  };

  return (
    <div className="space-y-6" data-testid="pay-periods-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pay Period Management</h1>
          <p className="text-slate-500 mt-1">Create and manage bi-weekly pay periods</p>
        </div>
        <Button 
          className="bg-teal-600 hover:bg-teal-700"
          onClick={() => setCreateDialogOpen(true)}
          data-testid="create-period-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Pay Period
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pay Periods</CardTitle>
          <CardDescription>All configured pay periods for timesheet tracking</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : payPeriods.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">No pay periods created yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period Name</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payPeriods.map((period) => (
                  <TableRow key={period.id} data-testid={`period-row-${period.id}`}>
                    <TableCell className="font-medium">{period.name}</TableCell>
                    <TableCell>{format(parseISO(period.start_date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>{format(parseISO(period.end_date), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <Badge className={period.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                        {period.status === 'open' ? <Unlock className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                        {period.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleToggleStatus(period)}
                        data-testid={`toggle-status-${period.id}`}
                      >
                        {period.status === 'open' ? (
                          <>
                            <Lock className="h-4 w-4 mr-1" />
                            Close
                          </>
                        ) : (
                          <>
                            <Unlock className="h-4 w-4 mr-1" />
                            Reopen
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Pay Period</DialogTitle>
            <DialogDescription>Create a new bi-weekly pay period</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Period Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Mar 9 - Mar 22, 2025"
                  required
                  data-testid="period-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="end-date-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading}
                data-testid="create-period-submit"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
